import { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useBookingForm from '../../hooks/useBookingForm';
import { useBookingContext } from '../../context/BookingContext';
import { toUTCISOString } from '../../utils/DateUtil';

const EditBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state, dispatch } = useBookingContext();
  const { users, vehicles } = state;

  const [initialData, setInitialData] = useState(null);
  const [isBookingLoading, setIsBookingLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    form,
    setForm,
    loading,
    setLoading,
    error: formError,
    handleChange,
    handleOfficeChange,
  } = useBookingForm(initialData, dispatch);

  // Fetch booking
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get(`/api/car-bookings/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setInitialData(res.data);
        setIsBookingLoading(false);
        
      } catch (err) {
        console.error('Failed to fetch booking:', err);
        setError('Failed to load booking');
      }
    };

    fetchBooking();
  }, [id, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const payload = {
      ...form,
      startDate: toUTCISOString(form.startDate), // local → UTC
      endDate:   toUTCISOString(form.endDate),
    };

    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/car-bookings/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/bookings');
    } catch (err) {
      console.error('Failed to update booking:', err);
      setError('Failed to update booking.');
    } finally {
      setLoading(false);
    }
  };

  if (isBookingLoading || users.length === 0) {
    return <div className="container mt-4 text-center">Loading booking form...</div>;
  }

  return (
    <div className="container mt-4">
      <div className="col-md-8 offset-md-2 text-start">
        <h2>Edit Booking</h2>
        {error && <div className="alert alert-danger">{error}</div>}
         {formError && <div className="alert alert-danger">{formError}</div>}

        <form onSubmit={handleSubmit}>

          <div className="row mb-3">
            <div className="col">
              <label className="form-label">Start Date & Time</label>
              <input
                type="datetime-local"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="col">
              <label className="form-label">End Date & Time</label>
              <input
                type="datetime-local"
                name="endDate"
                value={form.endDate}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Purpose</label>
            <input type="text" name="purpose" className="form-control" value={form.purpose} onChange={handleChange} required />
          </div>

          <div className="mb-3">
            <label className="form-label">Destination</label>
            <input type="text" name="destination" className="form-control" value={form.destination} onChange={handleChange} required />
          </div>

          <div className="mb-3">
            <label className="form-label">Office</label>
            <select name="office" className="form-select" value={form.office} onChange={handleOfficeChange} required>
              <option value="">-- Select Office --</option>
              <option value="HQ">HQ</option>
              <option value="SITE">Site</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Preferred Vehicle</label>
            <select name="preferredVehicle" className="form-select" value={form.preferredVehicle} onChange={handleChange} required>
              <option value="">-- Select Vehicle --</option>
              {vehicles.map(v => (
                <option key={v._id} value={v._id}>{v.name} ({v.plateNumber})</option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Approver</label>
            <select name="approver" className="form-select" value={form.approver} onChange={handleChange} required>
              <option value="">-- Select Approver --</option>
              {users
                .filter(u => u._id !== user?._id)
                .map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Add passenger */}
          <div className="mb-3">
            <label className="form-label">Add Passenger</label>
            <div className="input-group">
              <select className="form-select" value={form.passengerToAdd} onChange={(e) => setForm(prev => ({ ...prev, passengerToAdd: e.target.value }))}>
                <option value="">-- Select --</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
              <button type="button" className="btn btn-outline-primary" onClick={() => {
                if (form.passengerToAdd && !form.userPassengers.includes(form.passengerToAdd)) {
                  setForm(prev => ({
                    ...prev,
                    userPassengers: [...prev.userPassengers, prev.passengerToAdd],
                    passengerToAdd: '',
                  }));
                }
              }}>Add</button>
            </div>
          </div>

          {/* Selected passengers */}
          {form.userPassengers.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Selected Passengers</label>
              <ul className="list-group">
                {form.userPassengers.map(pid => {
                  const p = users.find(u => u._id === pid);
                  return (
                    <li key={pid} className="list-group-item d-flex justify-content-between align-items-center">
                      {p?.name || 'Unknown'}
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          userPassengers: prev.userPassengers.filter(id => id !== pid)
                        }));
                      }}>Remove</button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">Other Passengers</label>
            <input
              type="text"
              name="nonUserPassengers"
              className="form-control"
              value={form.nonUserPassengers}
              onChange={handleChange}
              placeholder="Comma-separated e.g. John Doe, Jane Smith"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Booking'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditBooking;
